import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, KeyRound, LockKeyhole } from 'lucide-react'
import { Reveal } from '@/components/generals/reveal'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ButtonLink } from '@/components/generals/button'

/*
   Two ways in
   The deliberate split between the public buying journey (wide sand card)
   and the discreet, secure resident entrance (narrower deep green card).
*/
export const PathwaysSection: FC = (): ReactNode => {
    return (
        <section id='buyers-and-residents' aria-label='Buyers and residents' className='bg-cream'>
            <div className='mx-auto grid max-w-7xl gap-5 px-6 py-20 lg:grid-cols-12 lg:px-10 lg:py-24'>
                <Reveal className='lg:col-span-7'>
                    <article className='flex h-full flex-col justify-between rounded-3xl border border-stone bg-sand p-8 sm:p-12'>
                        <div>
                            <Eyebrow>
                                For buyers
                            </Eyebrow>

                            <h2 className='mt-6 max-w-lg font-heading text-3xl leading-tight sm:text-4xl'>
                                A rare release of fourteen level homes
                            </h2>

                            <p className='mt-5 max-w-xl text-[0.9375rem] leading-relaxed text-muted-foreground'>
                                Freestanding, single-storey three-bedroom simplexes with open-plan living, level gardens, built-in fireplaces and double garages. Made for people who are ready for less upkeep and more living.
                            </p>
                        </div>

                        <div className='mt-10 flex flex-wrap items-center gap-x-8 gap-y-4'>
                            <ButtonLink href='#residences'>
                                View the residences
                                <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/button:translate-x-0.5' />
                            </ButtonLink>

                            <Link href='#enquire' className='link-underline pb-0.5 text-sm font-medium text-forest'>
                                Book a private viewing
                            </Link>
                        </div>
                    </article>
                </Reveal>

                <Reveal delay={120} className='lg:col-span-5'>
                    <article className='relative flex h-full flex-col justify-between overflow-hidden rounded-3xl bg-forest p-8 text-cream sm:p-12'>
                        <KeyRound aria-hidden='true' className='absolute -right-8 -top-8 size-44 rotate-12 text-cream/[0.05]' />

                        <div className='relative'>
                            <Eyebrow tone='light'>
                                For residents
                            </Eyebrow>

                            <h2 className='mt-6 font-heading text-3xl leading-tight sm:text-4xl'>
                                Resident Access
                            </h2>

                            <p className='mt-5 text-[0.9375rem] leading-relaxed text-cream/75'>
                                Estate notices, rules and documents to download, and visitor bookings for the gate, all behind a secure, personal sign-in.
                            </p>
                        </div>

                        <div className='relative mt-10 flex flex-wrap items-center gap-x-8 gap-y-4'>
                            <ButtonLink href='/residents' variant='light'>
                                <LockKeyhole aria-hidden='true' className='size-4' />
                                Sign in to the portal
                            </ButtonLink>

                            <Link href='/residents/access' className='link-underline pb-0.5 text-sm text-cream/80 transition-colors hover:text-cream'>
                                First time here?
                            </Link>
                        </div>
                    </article>
                </Reveal>
            </div>
        </section>
    )
}
